import React, { useState } from 'react';
import moment from 'moment';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  TextField,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import styled from '@emotion/styled';
import { useSnackbar } from 'notistack';
import {
  useStaffUpdateCredits,
  useGetCreditExtendReasons,
  useGetCreditExpireReasons
} from '@/api/finance/staff';
import { makeStyles } from '@mui/styles';

import CustomDatePickerField from '@/components/CustomDatePicker';
import { convertToStoreLocalTime } from '@/common/utils/time';
import {
  useElevatedRoleAction,
  ELEVATED_ROLE_ACTIONS
} from '@/hooks/useElevatedRoleAction';
import { validateCreditExtensionDate, getOriginalExpiryDate } from './utils';

const formatDate = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  return convertToStoreLocalTime(dateString, 'MMMM Do, YYYY');
};

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
    padding: 0;
    width: 100%;
    max-width: 667px;
    @media (max-width: 900px) {
      max-width: 100%;
    }
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 0;
  background: #fafafa;
  min-height: 68px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 0 26px;
`;

const InfoIconContainer = styled(Box)`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2f4fd;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleText = styled(Typography)`
  font-weight: 600;
  font-size: 20px;
  color: #222;
  text-align: left;
  margin-left: 12px;
`;

const StyledDialogContent = styled(DialogContent)`
  padding-top: 24px;
  padding-bottom: 16px;
  padding-left: 32px;
  padding-right: 32px;
`;

const DescriptionText = styled(Typography)`
  color: #00000099;
  font-weight: 400;
  font-size: 14px;
  padding: 16px 0;
`;

const BundleInfo = styled(Box)`
  margin-top: 16px;
  margin-bottom: 32px;
`;

const BundleName = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #000;
`;

const BundleDetail = styled(Typography)`
  color: #606b8a;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const SectionTitle = styled(Typography)`
  font-weight: 600;
  font-size: 12px;
  color: #000;
  margin-bottom: 8px;
`;

const StyledFormControl = styled(FormControl)`
  background: #fafafa;
  border-radius: 4px;
  width: 100%;
  max-width: 250px;
`;

const StyledSelect = styled(Select)`
  font-size: 14px;
  height: 40px;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 14px;
`;

const StyledTextField = styled(TextField)`
  background: #fafafa;
  border-radius: 4px;
  font-size: 15px;
`;

const StyledDialogActions = styled(DialogActions)`
  padding: 16px 26px 24px;
  justify-content: flex-end;
  background: #fafafa;
`;

const StyledButton = styled(Button)`
  min-width: 120px;
  font-weight: 600;
  font-size: 15px;
  border-radius: 4px;
`;

const StyledIconButton = styled(IconButton)`
  padding: 8px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  color: rgba(0, 0, 0, 0.6);
`;

const StyledExpireButton = styled(Button)`
  border: 1px solid #545d78;
  color: #545d78;
  font-weight: 600;
  font-size: 14px;
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 8px;
  width: 100%;
`;

const useStyles: any = makeStyles(() => ({
  datepickerContainer: {
    maxWidth: 250,
    height: 46,
    marginTop: 5,
    '& fieldset': {
      height: 45
    },
    '& .MuiInputBase-input': {
      padding: '9px 40px',
      width: 168
    },
    '& svg': {
      top: 10,
      left: 11,
      color: 'rgba(0, 0, 0, 0.6)'
    }
  }
}));

const API_ERRORS = {
  INVALID_QUANTITY: 'Invalid credit quantity was provided.',
  INVALID_CUSTOMER_CREDIT: 'Invalid customer credit ID provided.',
  INVALID_STORE: 'Invalid store ID provided.',
  INVALID_EXPIRATION_DATE: 'Invalid expiration date provided.',
  CREDIT_EXTENSION_LIMIT_EXCEEDED: 'Credit extension limit exceeded.',
  CREDIT_CANNOT_BE_EXTENDED: 'Credit cannot be extended.',
  FAILED_UPDATING_CUSTOMER_CREDITS: 'Failed updating customer credits.',
  FAILED_LOGGING_CUSTOMER_CREDITS: 'The logging of this credit change failed.',
  UNKNOWN: 'An unknown error occurred.'
};

type ApiError = Error & {
  response?: {
    data?: {
      error_code?: string;
    };
  };
};

const getErrorDetails = (error: ApiError) => {
  const errorCode = error?.response?.data?.error_code;
  if (errorCode && errorCode in API_ERRORS) {
    return API_ERRORS[errorCode as keyof typeof API_ERRORS];
  }
  return API_ERRORS.UNKNOWN;
};

interface ExtendCreditsModalProps {
  open: boolean;
  onClose: () => void;
  bundleName: string;
  ingredient: string;
  expiryDate: string;
  onSave: () => void;
  mode?: 'extend' | 'expire';
  selectedCredit?: any;
  customerId?: number;
}

const ExtendCreditsModal = ({
  open,
  onClose,
  bundleName,
  ingredient,
  expiryDate,
  onSave,
  mode = 'extend',
  selectedCredit
}: ExtendCreditsModalProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [showTimeFormat, setShowTimeFormat] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  let actionType;
  if (mode === 'extend') {
    actionType = ELEVATED_ROLE_ACTIONS.EXTEND_CREDITS;
  } else if (mode === 'expire') {
    actionType = ELEVATED_ROLE_ACTIONS.EXPIRE_CREDITS;
  }

  // Manager approval for extend mode
  const { isReady, transport } = useElevatedRoleAction({
    actionType: actionType,
    onCancel: () => onClose(),
    promptMessage: "Enter manager's pin to extend credits."
  });

  // Mutation hook with elevated transport
  const { mutateAsync: updateCredits } = useStaffUpdateCredits({
    request: { axiosInstance: transport }
  });

  // Query hooks for reasons
  const extendReasons = useGetCreditExtendReasons({
    query: {
      enabled: open && mode === 'extend'
    }
  });
  const expireReasons = useGetCreditExpireReasons({
    query: {
      enabled: open && mode === 'expire'
    }
  });

  const reasons =
    (mode === 'extend' ? extendReasons.data?.data : expireReasons.data?.data) ||
    [];

  // Reset date when modal opens
  React.useEffect(() => {
    if (open) {
      const defaultDate = getDefaultDate();
      setDate(defaultDate);
    }
  }, [open, mode]);

  // Generate credit options based on quantity
  const credits = React.useMemo(() => {
    if (!selectedCredit?.quantity) {
      return [];
    }

    const quantity = selectedCredit.quantity;
    const options = [];

    for (let i = 1; i <= quantity; i++) {
      options.push(i.toString());
    }

    return options;
  }, [selectedCredit?.quantity]);

  // Calculate default date based on mode
  const getDefaultDate = () => {
    if (mode === 'extend') {
      // For extend: always use the original expiry date as default
      // Use original_expiry if available, otherwise fall back to current expiry
      const dateToUse = selectedCredit?.original_expiry || expiryDate;
      if (dateToUse) {
        return convertToStoreLocalTime(dateToUse, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]');
      }
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      return futureDate.toISOString();
    }
    // For expire: default to today
    return new Date().toISOString();
  };

  // Calculate max date based on mode
  const getMaxDate = () => {
    if (!expiryDate) {
      return null;
    }

    if (mode === 'extend') {
      // For extend: original expiry + 58 days (60 days total including original)
      // Note: Using 58 instead of 59 due to timezone conversion issues with Date objects
      // This ensures the correct maximum date is displayed in the calendar picker
      // Use original_expiry if available, otherwise fall back to current expiry
      const originalExpiry = selectedCredit?.original_expiry || expiryDate;
      const maxExtensionDate = new Date(originalExpiry);
      maxExtensionDate.setDate(maxExtensionDate.getDate() + 58);
      return maxExtensionDate;
    } else {
      // For expire: original expiry date
      return new Date(expiryDate);
    }
  };

  const [date, setDate] = useState<string>(getDefaultDate());
  const [reason, setReason] = useState<string>('');
  const [details, setDetails] = useState<string>('');
  const [reasonOpen, setReasonOpen] = useState<boolean>(false);
  const [selectedCreditOption, setSelectedCreditOption] = useState<string>('');
  const [errors, setErrors] = useState<{
    date?: boolean;
    reason?: boolean;
    details?: boolean;
  }>({});

  const isExtendDisabled = React.useMemo(() => {
    if (mode !== 'extend') {
      return false;
    }

    // Check if credit was refunded
    if (selectedCredit?.is_refunded) {
      return true;
    }

    const maxDate = getMaxDate();
    // Use original_expiry for validation if available, otherwise use current expiry
    const dateForValidation = selectedCredit?.original_expiry || expiryDate;
    const validation = validateCreditExtensionDate(
      date,
      dateForValidation,
      maxDate
    );

    return validation.isDateBeforeOriginalExpiry || validation.isDateExceedsMax;
  }, [
    mode,
    selectedCredit?.is_refunded,
    date,
    expiryDate,
    selectedCredit?.original_expiry,
    getMaxDate
  ]);

  const shouldShowTooltip = React.useMemo(() => {
    if (mode !== 'extend') {
      return false;
    }

    // Show tooltip if credit was refunded
    if (selectedCredit?.is_refunded) {
      return true;
    }

    const maxDate = getMaxDate();
    // Use original_expiry for validation if available, otherwise use current expiry
    const dateForValidation = selectedCredit?.original_expiry || expiryDate;
    const validation = validateCreditExtensionDate(
      date,
      dateForValidation,
      maxDate
    );

    return (
      validation.isDateBeforeOriginalExpiry === true ||
      validation.isDateExceedsMax === true
    );
  }, [mode, selectedCredit?.is_refunded, date, expiryDate, getMaxDate]);

  const getExtendTooltip = () => {
    if (mode !== 'extend') {
      return '';
    }

    // Check if credit was refunded
    if (selectedCredit?.is_refunded) {
      return 'Credit was refunded';
    }

    const maxDate = getMaxDate();
    // Use original_expiry for validation if available, otherwise use current expiry
    const dateForValidation = selectedCredit?.original_expiry || expiryDate;
    const originalExpiryDate = getOriginalExpiryDate(dateForValidation);
    const validation = validateCreditExtensionDate(
      date,
      dateForValidation,
      maxDate
    );

    if (validation.isDateBeforeOriginalExpiry) {
      return `Cannot extend before ${convertToStoreLocalTime(
        originalExpiryDate,
        'MM/dd/yyyy'
      )} (original expiry date)`;
    }
    if (validation.isDateExceedsMax) {
      return `Cannot extend past ${convertToStoreLocalTime(
        maxDate,
        'MM/dd/yyyy'
      )} (60 days from original expiry)`;
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {
      date: !date,
      reason: !reason,
      details: reason === 'Other' && !details
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSave = async () => {
    if (validateForm() && !isExtendDisabled) {
      setIsSubmitting(true);
      try {
        // Find the selected reason to get the reasonCode
        const selectedReasonData = reasons.find(r => r.reason === reason);
        const reasonCategory = selectedReasonData?.reasonCode || '';

        const payload = {
          customerCreditIds: selectedCredit.ids,
          quantity: selectedCreditOption
            ? parseInt(selectedCreditOption)
            : selectedCredit?.quantity || 0,
          expires: moment(date).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          expiresNow: mode === 'expire' && showTimeFormat,
          reasonCategory: reasonCategory,
          reason: reason === 'Other' ? details : reason
        };

        await updateCredits({ data: payload });

        enqueueSnackbar(
          mode === 'extend'
            ? 'Credit successfully reactivated\nReview it in Remaining Credits now.'
            : 'Credit successfully expired\nView it in Expired Credits now.',
          {
            variant: 'success',
            style: { whiteSpace: 'pre-line' }
          }
        );

        onSave();
      } catch (error: any) {
        const msg = `Failed to ${mode} credit: ${getErrorDetails(error)}`;
        enqueueSnackbar(msg, {
          variant: 'error'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleExpireImmediately = () => {
    const now = new Date();
    setDate(now.toISOString());
    setShowTimeFormat(true);
    setErrors(prev => ({ ...prev, date: false }));
  };

  const handleClose = () => {
    setDate(getDefaultDate());
    setReason('');
    setDetails('');
    setReasonOpen(false);
    setSelectedCreditOption('');
    setErrors({});
    setShowTimeFormat(false);
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <StyledDialogTitle>
        <Grid container alignItems="center">
          <Grid item>
            <InfoIconContainer>
              <InfoIcon color="info" fontSize="small" />
            </InfoIconContainer>
          </Grid>
          <Grid item xs>
            <TitleText>
              {mode === 'extend'
                ? 'Extend credits expiration'
                : 'Expire credits'}
            </TitleText>
          </Grid>
          <Grid item>
            <StyledIconButton onClick={handleClose} size="small">
              <StyledCloseIcon />
            </StyledIconButton>
          </Grid>
        </Grid>
      </StyledDialogTitle>
      <StyledDialogContent>
        <DescriptionText>
          Modify the expiration date of credits below. You can select multiple
          credits and provide a reason for the change.
        </DescriptionText>
        <BundleInfo>
          <BundleName>{bundleName}</BundleName>
          <BundleDetail>{ingredient}</BundleDetail>
          <BundleDetail>Expiry date: {formatDate(expiryDate)}</BundleDetail>
        </BundleInfo>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} style={{ marginTop: '24px' }}>
            <SectionTitle>Credits</SectionTitle>
            <StyledFormControl>
              <StyledSelect
                value={selectedCreditOption}
                onChange={(e: React.ChangeEvent<{ value: string }>) => {
                  setSelectedCreditOption(e.target.value);
                }}
                displayEmpty
                renderValue={(selected: string) => {
                  if (!selected) {
                    return 'All Credits';
                  }
                  return selected;
                }}
                disabled={false}
              >
                <StyledMenuItem value="">
                  <em>All Credits</em>
                </StyledMenuItem>
                {credits.map(credit => (
                  <StyledMenuItem key={credit} value={credit}>
                    {credit}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} style={{ marginTop: '24px' }}>
            <SectionTitle>Date</SectionTitle>
            <Grid container spacing={3} alignItems="center">
              <Grid item style={{ minWidth: '274px' }}>
                <Box className={classes.datepickerContainer}>
                  <CustomDatePickerField
                    value={date || ''}
                    onChangeHandler={(newDate: string) => {
                      setDate(newDate);
                      setErrors(prev => ({ ...prev, date: false }));
                    }}
                    dateFormat={
                      showTimeFormat ? 'MM/dd/yyyy, hh:mm a' : 'MM/dd/yyyy'
                    }
                    maxDate={getMaxDate()}
                    minDate={
                      mode === 'extend'
                        ? new Date(
                            selectedCredit?.original_expiry || expiryDate
                          )
                        : new Date()
                    }
                    error={errors.date}
                    helperText={errors.date ? 'Date is required' : ''}
                  />
                </Box>
              </Grid>
              {mode === 'expire' && (
                <Grid item xs={4}>
                  <StyledExpireButton
                    variant="outlined"
                    fullWidth
                    onClick={handleExpireImmediately}
                  >
                    Expire Immediately
                  </StyledExpireButton>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginTop: '24px' }}>
            <SectionTitle>Reason for Extension</SectionTitle>
            <StyledFormControl error={errors.reason}>
              <StyledSelect
                displayEmpty
                value={reason}
                onChange={(e: React.ChangeEvent<{ value: string }>) => {
                  setReason(e.target.value);
                  setReasonOpen(e.target.value === 'Other');
                  setErrors(prev => ({ ...prev, reason: false }));
                }}
                renderValue={(selected: string) => {
                  if (!selected) {
                    return 'Select a reason for expiration...';
                  }
                  return selected;
                }}
              >
                {reasons.map(r => (
                  <StyledMenuItem key={r.reason} value={r.reason}>
                    {r.reason}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          </Grid>
          {reasonOpen && (
            <Grid item xs={12} style={{ marginTop: '24px' }}>
              <SectionTitle>Please specify the reason</SectionTitle>
              <StyledTextField
                placeholder="Enter reason here.."
                value={details}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDetails(e.target.value);
                  setErrors(prev => ({ ...prev, details: false }));
                }}
                fullWidth
                multiline
                minRows={2}
                error={errors.details}
                InputLabelProps={{ sx: { fontSize: 15 } }}
                InputProps={{ sx: { fontSize: 15 } }}
              />
            </Grid>
          )}
        </Grid>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton
          onClick={handleClose}
          variant="outlined"
          sx={{ marginRight: '8px' }}
          disabled={isSubmitting}
        >
          Cancel
        </StyledButton>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Tooltip
            title={getExtendTooltip()}
            placement="top"
            arrow
            open={shouldShowTooltip}
          >
            <span>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleSave}
                loading={!isReady || isSubmitting}
                disabled={!isReady}
                sx={{
                  minWidth: '120px',
                  fontWeight: 600,
                  fontSize: '15px',
                  borderRadius: '4px'
                }}
              >
                Save
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ExtendCreditsModal;
